/* =========================================================
   Life InStyle — product page gallery. sections/instyle-product.liquid

   Accents-style (2026-10-02): on desktop a vertical thumbnail strip beside a
   large square photo with arrows and the left/right keys; on phones a
   swipeable square carousel with a "2 / 5" counter.

   The photos are ONE native scroll-snap row on every device. The browser
   does the swiping (momentum, rubber-banding, accessibility); this file keeps
   the thumbnails, arrows and counter in step with it, and moves the row when
   a thumbnail, an arrow, a key or a size choice asks for another photo.

   Rules carried from context/scroll-animation-rules.md and the iOS notes:
   - the active photo is read from the row's own scroll position, never from
     a flag we set, because iOS can drop a programmatic scroll mid-gesture
     and fire no event for it;
   - a failed measurement (width 0) is never read as "first photo";
   - nothing plays on its own. A video loads only when its play button is
     pressed, and any video — <video>, YouTube or Vimeo — is paused as soon as
     its photo leaves the frame;
   - auto-rotation is OFF unless the section setting turns it on. When on, it
     never runs off screen, in a hidden tab, or under reduced motion, and a
     person touching the gallery stops it (its own scrolling never does).

   Public behaviour kept from the first version: a size or choice with its
   own photo brings that photo into the frame (Dawn's variantChange event),
   and the phone bar (instyle-pdp-sticky.js) is independent of this file.

   Loaded from inside <product-info>, so Dawn's quick-add modal re-runs this
   script each time it opens; the guard below makes a re-run only initialise
   new galleries instead of stacking listeners.
   ========================================================= */
(function () {
  if (window.InStylePdpGallery) {
    window.InStylePdpGallery.initAll();
    return;
  }

  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function pauseIn(el) {
    if (!el) return;
    el.querySelectorAll('video').forEach(function (v) {
      if (!v.paused) v.pause();
    });
    el.querySelectorAll('iframe.js-youtube').forEach(function (f) {
      if (f.contentWindow) f.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    });
    el.querySelectorAll('iframe.js-vimeo').forEach(function (f) {
      if (f.contentWindow) f.contentWindow.postMessage('{"method":"pause"}', '*');
    });
  }

  /* The square photo is never taller than the screen minus the header. The
     header's height changes with the announcement bar and logo size, so it is
     measured rather than assumed; CSS falls back to 12rem until this runs. */
  function measureHeader() {
    var h = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach(function (s) {
      h += s.offsetHeight;
    });
    if (h > 0) document.documentElement.style.setProperty('--instyle-pdp-header-h', h + 'px');
  }

  function initGallery(root) {
    var gallery = root.querySelector('[data-pdp-gallery]');
    if (!gallery || gallery.dataset.pdpReady) return;
    var track = gallery.querySelector('[data-pdp-track]');
    if (!track) return;
    gallery.dataset.pdpReady = '1';

    var slides = Array.prototype.slice.call(track.children);
    var thumbList = gallery.querySelector('[data-pdp-thumbs]');
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-pdp-thumb]'));
    var prev = gallery.querySelector('[data-pdp-prev]');
    var next = gallery.querySelector('[data-pdp-next]');
    var count = gallery.querySelector('[data-pdp-count]');
    // In the quick-add modal Dawn renames the section id; its events still
    // carry the original one.
    var sectionId = root.dataset.originalSection || root.dataset.section;
    var last = slides.length - 1;
    var current = Math.max(0, Math.min(last, parseInt(gallery.dataset.start, 10) || 0));
    var ticking = false;

    function indexFromScroll() {
      var w = track.clientWidth;
      if (w <= 0) return current; // a failed measurement is not a position
      var i = Math.round(track.scrollLeft / w);
      return Math.max(0, Math.min(last, i));
    }

    function revealThumb(i) {
      var t = thumbs[i];
      if (!t || !thumbList) return;
      // Only the strip scrolls; scrollIntoView would also move the page.
      var item = t.parentElement || t;
      var top = item.offsetTop;
      var bottom = top + item.offsetHeight;
      var viewTop = thumbList.scrollTop;
      var viewBottom = viewTop + thumbList.clientHeight;
      if (thumbList.clientHeight <= 0) return;
      if (top < viewTop) thumbList.scrollTop = top;
      else if (bottom > viewBottom) thumbList.scrollTop = bottom - thumbList.clientHeight;
    }

    // Photos are lazy-loaded; the one about to be shown and its neighbours
    // are asked for early, so an arrow or a far thumbnail jump never lands on
    // an empty frame.
    function warm(i) {
      for (var k = i - 1; k <= i + 1; k++) {
        var s = slides[k];
        if (!s) continue;
        s.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
          img.loading = 'eager';
        });
      }
    }

    function mark(i, force) {
      if (i === current && !force) return;
      var was = current;
      current = i;
      warm(i);
      thumbs.forEach(function (t, k) {
        if (k === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (count) count.textContent = String(i + 1);
      if (prev) prev.disabled = i <= 0;
      if (next) next.disabled = i >= last;
      revealThumb(i);
      if (was !== i) pauseIn(slides[was]);
    }

    function go(i, smooth) {
      i = Math.max(0, Math.min(last, i));
      var s = slides[i];
      if (!s) return;
      warm(i);
      // Neighbours glide; a far jump (a thumbnail, a size choice) cuts
      // straight there instead of sweeping past every photo in between.
      var glide = smooth && !reduceMotion.matches && Math.abs(i - current) === 1;
      if (typeof track.scrollTo === 'function') {
        track.scrollTo({ left: s.offsetLeft, behavior: glide ? 'smooth' : 'auto' });
      } else {
        track.scrollLeft = s.offsetLeft;
      }
      mark(i);
    }

    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          mark(indexFromScroll());
        });
      },
      { passive: true }
    );

    thumbs.forEach(function (t) {
      var k = parseInt(t.dataset.pdpThumb, 10) || 0;
      t.addEventListener('click', function () {
        go(k, true);
      });
      // A hovered thumbnail is about to be clicked: fetch its photo now.
      t.addEventListener(
        'pointerenter',
        function () {
          warm(k);
        },
        { passive: true }
      );
    });
    if (prev)
      prev.addEventListener('click', function () {
        go(current - 1, true);
      });
    if (next)
      next.addEventListener('click', function () {
        go(current + 1, true);
      });

    // Left/right keys while the gallery has focus — not page-wide, so they
    // never steal the arrows from the size buttons — and never from inside a
    // playing video, whose own controls use those keys to seek.
    gallery.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.target.closest && e.target.closest('video, iframe, input, select, textarea')) return;
      e.preventDefault();
      go(current + (e.key === 'ArrowRight' ? 1 : -1), true);
    });

    // Play button: build the player from its template only now.
    gallery.querySelectorAll('[data-pdp-play]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrap = btn.closest('[data-pdp-video]');
        var tpl = wrap && wrap.querySelector('template');
        if (!wrap || !tpl || wrap.dataset.loaded) return;
        var player = tpl.content.firstElementChild && tpl.content.firstElementChild.cloneNode(true);
        if (!player) return;
        wrap.dataset.loaded = '1';
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        btn.hidden = true;
        wrap.appendChild(player);
        var v = player.nodeName === 'VIDEO' ? player : player.querySelector && player.querySelector('video');
        if (v) {
          var p = v.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        }
        if (typeof player.focus === 'function') player.focus({ preventScroll: true });
      });
    });

    // Keep the same photo in the frame when the WIDTH changes. A phone's
    // address bar collapsing changes only the height, and re-snapping then
    // would cut through a swipe in progress.
    var lastWidth = track.clientWidth;
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var w = track.clientWidth;
        if (w <= 0 || w === lastWidth) return;
        lastWidth = w;
        var s = slides[current];
        if (s) track.scrollLeft = s.offsetLeft;
      }, 120);
    });

    // A size or choice with its own photo brings that photo into the frame.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var data = event && event.data;
        if (!data || data.sectionId !== sectionId || !gallery.isConnected) return;
        var media = data.variant && data.variant.featured_media;
        if (!media) return;
        for (var k = 0; k < slides.length; k++) {
          if (slides[k].dataset.mediaId === String(media.id)) {
            go(k, true);
            return;
          }
        }
      });
    }

    // Open on the starting photo (the selected variant's). The section's
    // inline script has usually done this before the first paint already.
    if (current > 0) {
      var start = slides[current];
      if (start && track.clientWidth > 0) track.scrollLeft = start.offsetLeft;
    }
    mark(current, true);

    if (gallery.hasAttribute('data-autorotate') && slides.length > 1) autorotate();

    /* ---------- Auto-rotation (setting; OFF by default) --------------------
       Naman's spec for the old gallery, 2026-07-29: photos rotate on their
       own; once somebody interacts it switches to manual; after 30–40 s idle
       it starts again. Whether the new gallery rotates is undecided, so the
       setting defaults to off. */
    function autorotate() {
      if (reduceMotion.matches) return;
      var cycle = (parseFloat(gallery.dataset.autorotate) || 5) * 1000;
      var resume = (parseFloat(gallery.dataset.autorotateResume) || 30) * 1000;
      var timer = null;
      var resumeTimer = null; // doubles as "a person is driving"
      var onScreen = false;

      function stop() {
        clearInterval(timer);
        timer = null;
      }
      function play() {
        stop();
        if (!onScreen || document.hidden || resumeTimer || !gallery.isConnected) return;
        timer = setInterval(function () {
          if (gallery.querySelector('[data-pdp-video][data-loaded]')) return stop();
          go(current >= last ? 0 : current + 1, true);
        }, cycle);
      }
      function handover() {
        stop();
        clearTimeout(resumeTimer);
        resumeTimer = setTimeout(function () {
          resumeTimer = null;
          play();
        }, resume);
      }
      // People touch, click, wheel and type. The row's own `scroll` events
      // are deliberately not on this list: our advances cause them.
      ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (evt) {
        gallery.addEventListener(evt, handover, { passive: true });
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            onScreen = entries[entries.length - 1].isIntersecting;
            if (onScreen) play();
            else stop();
          },
          { threshold: 0.35 }
        ).observe(gallery);
      } else {
        onScreen = true;
        play();
      }
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
        else play();
      });
    }
  }

  function initAll() {
    measureHeader();
    document.querySelectorAll('[data-instyle-pdp]').forEach(initGallery);
  }

  window.InStylePdpGallery = { initAll: initAll, init: initGallery };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  window.addEventListener('load', measureHeader);
  var headerTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(headerTimer);
    headerTimer = setTimeout(measureHeader, 150);
  });
  // Theme editor re-renders the section in place; Dawn's product swap and the
  // quick-add modal announce a fresh <product-info> with this event.
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('product-info:loaded', initAll);
})();
